import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BrainIcon, TrendingDownIcon, AlertTriangleIcon } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import useTranslation from '@/hooks/use-translation';
import type { Mode } from '@/contexts/ModeContext';

interface InsightsCardProps {
  mode: Mode;
}

const InsightsCard: React.FC<InsightsCardProps> = ({ mode }) => {
  const { isRTL } = useLanguage();
  const { translate } = useTranslation();

  const getInsights = () => {
    if (mode === 'household') {
      return [
        {
          icon: <TrendingDownIcon className="h-5 w-5 text-green-500" />,
          text: translate("Your water usage is 15% lower than last month.")
        },
        {
          icon: <AlertTriangleIcon className="h-5 w-5 text-yellow-500" />,
          text: translate("Unusual water activity detected between 2-4 AM. Possible leak?")
        },
        {
          icon: <BrainIcon className="h-5 w-5 text-blue-500" />,
          text: translate("Consider installing low-flow showerheads to save up to 2,700 gallons annually.")
        }
      ];
    } else {
      return [
        {
          icon: <TrendingDownIcon className="h-5 w-5 text-green-500" />,
          text: translate("Irrigation efficiency improved by 20% this season.")
        },
        {
          icon: <AlertTriangleIcon className="h-5 w-5 text-yellow-500" />,
          text: translate("Field sector B shows signs of over-irrigation. Adjust sprinkler settings.")
        },
        {
          icon: <BrainIcon className="h-5 w-5 text-blue-500" />,
          text: translate("Predicted rainfall next week. Consider adjusting irrigation schedule.")
        }
      ];
    }
  };

  const insights = getInsights();

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {translate(mode === 'household' ? "Smart Insights" : "Irrigation Insights")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {insights.map((insight, index) => (
            <div 
              key={index}
              className="flex items-start gap-3 p-3 rounded-lg bg-muted/50"
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              {insight.icon}
              <p className="text-sm">{insight.text}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default InsightsCard;
